import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { AuthAlert, AuthPageLayout } from './AuthPageLayout';
import { resolveReturnPath } from '../utils/loginReturnUrl';

export const SsoCompletePage: React.FC = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const { completeSsoLogin, isAuthenticated } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  const search = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = search.get('code') || '';
  const returnPath = resolveReturnPath(location.search);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, navigate, returnPath]);

  React.useEffect(() => {
    if (!code) {
      setError(t('errors.ssoFailed'));
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        await completeSsoLogin(code);
        if (!cancelled) {
          navigate(returnPath, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('errors.ssoFailed'));
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [code, completeSsoLogin, navigate, returnPath, t]);

  return (
    <AuthPageLayout title={t('ssoCompletingTitle')} subtitle={t('ssoCompletingSubtitle')}>
      {error ? <AuthAlert variant="error">{error}</AuthAlert> : <p>{t('ssoCompletingSubtitle')}</p>}
    </AuthPageLayout>
  );
};

export default SsoCompletePage;
